import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { addFeedback } from '../../../../redux/actions/Feedback';
import { useSelector } from 'react-redux';
import { useColorMode } from '@chakra-ui/react'; // Keep this for theme detection
import PaymentFrom from '../../../../components/SerialNumber/PaymentFrom';

export default function FeedbackForm(props) {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const [isOpen, setIsOpen] = useState(false);
   const { isFeedbackOpen, setIsFeedbackOpen } = props;
   const dispatch = useDispatch();
   const [droppedFiles, setDroppedFiles] = useState([]);
   const [formData, setFormData] = useState({
      type: '',
      comment: '',
      screenshot: [],
   });

   const textColor = isDark ? 'text-white' : 'text-gray-900';

   const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
         ...prev,
         [name]: value,
      }));
   };

   const handleDrop = (e) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files).filter((file) => file.type.startsWith('image/'));
      setDroppedFiles((prev) => [...prev, ...files]);
   };

   const handleFileSelect = (e) => {
      const files = Array.from(e.target.files).filter((file) => file.type.startsWith('image/'));
      setDroppedFiles((prev) => [...prev, ...files]);
   };

   const handleAddImages = () => {
      setFormData((prev) => ({
         ...prev,
         screenshot: [...prev.screenshot, ...droppedFiles],
      }));
      setDroppedFiles([]);
      setIsOpen(false);
   };

   const handleSave = () => {
      const fd = new FormData();
      fd.append('comment', formData.comment);
      fd.append('type', formData.type);

      formData.screenshot.forEach((file) => {
         fd.append('screenshot', file);
      });
      dispatch(addFeedback(fd));
   };

   return (
      <>
         <div className="rounded-xl w-full max-w-[500px] ml-auto" {...props}>
            <div className="flex items-center justify-center mb-6">
               <h2 className={`text-2xl font-bold ${textColor}`}>Feedback</h2>
            </div>

            <form onSubmit={(e) => e.preventDefault()}>
               {/* Type Field */}
               <div className="mb-4">
                  <label className={`block mb-2 font-medium ${textColor}`}>Type</label>
                  <select
                     name="type"
                     value={formData.type}
                     onChange={handleChange}
                     className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                     }`}>
                     <option value="">Select feedback type</option>
                     <option value={1}>Error</option>
                     <option value={2}>Review</option>
                     <option value={3}>Suggestion</option>
                  </select>
               </div>

               {/* Comment Field */}
               <div className="mb-4">
                  <label className={`block mb-2 font-medium ${textColor}`}>Comment</label>
                  <textarea
                     name="comment"
                     value={formData.comment}
                     onChange={handleChange}
                     placeholder="Write your comment"
                     rows="4"
                     className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDark
                           ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400'
                           : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                     }`}
                  />
               </div>

               {/* Upload Image */}
               <div className="mb-4 flex items-center justify-between">
                  <label className={`font-medium ${textColor}`}>Upload image</label>
                  <button
                     type="button"
                     onClick={() => setIsOpen(true)}
                     className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                     Add image
                  </button>
               </div>

               {/* Screenshot List */}
               <ul className="space-y-2">
                  {formData.screenshot.map((file, idx) => (
                     <li
                        key={idx}
                        className={`border-2 rounded-md p-1 flex justify-between items-center ${
                           isDark ? 'border-gray-600' : 'border-gray-200'
                        }`}>
                        <button
                           type="button"
                           className="text-blue-500 hover:text-blue-600 text-sm max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-left"
                           onClick={() => {
                              const url = URL.createObjectURL(file);
                              window.open(url, '_blank');
                           }}>
                           {file.name}
                        </button>

                        <button
                           type="button"
                           className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-1 rounded text-sm"
                           onClick={() => {
                              setFormData((prev) => ({
                                 ...prev,
                                 screenshot: prev.screenshot.filter((_, i) => i !== idx),
                              }));
                           }}>
                           ✖
                        </button>
                     </li>
                  ))}
               </ul>

               {/* Submit Button */}
               <button
                  type="submit"
                  onClick={handleSave}
                  disabled={!formData.type || !formData.comment}
                  className={`w-full mt-3 px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors ${
                     !formData.type || !formData.comment
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-500 text-white hover:bg-green-600'
                  }`}>
                  Submit Feedback
               </button>
            </form>
         </div>

         {/* Modal */}
         {isOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
               {/* Overlay */}
               <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsOpen(false)} />

               {/* Modal Content */}
               <div
                  className={`relative z-10 rounded-lg shadow-xl max-w-lg w-full mx-4 ${
                     isDark ? 'bg-gray-800' : 'bg-white'
                  }`}>
                  {/* Modal Header */}
                  <div
                     className={`flex items-center justify-between p-6 border-b ${
                        isDark ? 'border-gray-700' : 'border-gray-200'
                     }`}>
                     <h3 className={`text-xl font-semibold ${textColor}`}>Add screenshot</h3>
                     <button
                        onClick={() => setIsOpen(false)}
                        className={`text-2xl leading-none hover:opacity-70 ${textColor}`}>
                        ×
                     </button>
                  </div>

                  {/* Modal Body */}
                  <div className="p-6">
                     <div
                        onDrop={handleDrop}
                        onDragOver={(e) => e.preventDefault()}
                        className={`border-2 border-dashed rounded-md p-6 text-center mb-4 ${
                           isDark ? 'border-gray-600' : 'border-gray-300'
                        }`}>
                        <p className={`font-bold mb-2 ${textColor}`}>Drag and drop images here</p>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                           or click below to browse
                        </p>
                     </div>

                     <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                        className={`w-full p-1 border rounded-md ${
                           isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                     />

                     {droppedFiles.length > 0 && (
                        <div className="mt-4 space-y-2">
                           {droppedFiles.map((file, i) => (
                              <p key={i} className={`text-sm ${textColor}`}>
                                 ✅ {file.name}
                              </p>
                           ))}
                        </div>
                     )}
                  </div>

                  {/* Modal Footer */}
                  <div
                     className={`flex justify-end gap-3 p-6 border-t ${
                        isDark ? 'border-gray-700' : 'border-gray-200'
                     }`}>
                     <button
                        onClick={handleAddImages}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500">
                        Add
                     </button>
                     <button
                        onClick={() => setIsOpen(false)}
                        className={`px-4 py-2 rounded-md hover:bg-opacity-80 focus:outline-none focus:ring-2 ${
                           isDark
                              ? 'bg-gray-700 text-white focus:ring-gray-600'
                              : 'bg-gray-100 text-gray-700 focus:ring-gray-400'
                        }`}>
                        Cancel
                     </button>
                  </div>
               </div>
            </div>
         )}
      </>
   );
}

// import {
//   Box,
//   Flex,
//   Text,
//   Textarea,
//   Button,
//   Select,
//   useColorModeValue,
//   FormControl,
//   FormLabel,
//   Modal,
//   ModalOverlay,
//   ModalContent,
//   ModalHeader,
//   ModalBody,
//   ModalFooter,
//   ModalCloseButton,
//   useDisclosure,
//   useToast,
//   VStack,
//   Input,
//   List,
//   ListItem,
// } from "@chakra-ui/react";
// import React, { useState } from "react";
// import { useDispatch } from 'react-redux';
// import { addFeedback } from "../../../../redux/actions/Feedback";
// import { useSelector } from 'react-redux';
// import SerialNumber from '../../../../components/SerialNumber/SerialNumber'

// export default function FeedbackForm(props) {
//   const { isOpen, onOpen, onClose } = useDisclosure();
//   const toast = useToast();
//   const dispatch = useDispatch();
//   const [droppedFiles, setDroppedFiles] = useState([]);
//   const [formData, setFormData] = useState({
//     type: "",
//     comment: "",
//     screenshot: [],
//   });

//   const textColor = useColorModeValue("secondaryGray.900", "white");

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };

//   const handleDrop = (e) => {
//     e.preventDefault();
//     const files = Array.from(e.dataTransfer.files).filter((file) =>
//       file.type.startsWith("image/")
//     );
//     setDroppedFiles((prev) => [...prev, ...files]);
//   };

//   const handleFileSelect = (e) => {
//     const files = Array.from(e.target.files).filter((file) =>
//       file.type.startsWith("image/")
//     );
//     setDroppedFiles((prev) => [...prev, ...files]);
//   };

//   const handleAddImages = () => {
//     setFormData((prev) => ({
//       ...prev,
//       screenshot: [...prev.screenshot, ...droppedFiles],
//     }));
//     setDroppedFiles([]);
//     onClose();
//   };

//   const handleSave = () => {
//     const fd = new FormData();
//     fd.append("comment", formData.comment)
//     fd.append("type", formData.type)

//     formData.screenshot.forEach((file) => {
//       fd.append("screenshot", file);
//     } )
//     dispatch (addFeedback(fd));

//   };

//   return (
//     <>
//       <Box
//         p="20px"
//         bg={useColorModeValue("white", "navy.700")}
//         borderRadius="xl"
//         shadow="md"
//         w="100%"
//         maxW="600px"
//         mx="auto"
//         {...props}
//       >
//         <Flex align="center" justify="center" mb="6">
//           <Text fontSize="2xl" fontWeight="bold" color={textColor}>
//             Feedback
//           </Text>
//         </Flex>

//         <Box as="form" onSubmit={(e) => e.preventDefault()}>
//           <FormControl mb="4">
//             <FormLabel color={textColor}>Type</FormLabel>
//             <Select
//               name="type"
//               value={formData.type}
//               onChange={handleChange}
//               placeholder="Select feedback type"
//             >
//               <option value={1}>Error</option>
//               <option value={2}>Review</option>
//               <option value={3}>Suggestion</option>
//             </Select>
//           </FormControl>

//           <FormControl mb="4">
//             <FormLabel color={textColor}>Comment</FormLabel>
//             <Textarea
//               name="comment"
//               value={formData.comment}
//               onChange={handleChange}
//               placeholder="Write your comment"
//             />
//           </FormControl>

//           <FormControl mb="4" display="flex" alignItems="center" justifyContent="space-between">
//             <FormLabel color={textColor}>Upload image</FormLabel>
//             <Button onClick={onOpen} colorScheme="blue">
//               Add image
//             </Button>
//           </FormControl>

//           <List spacing={2}>
//             {formData.screenshot.map((file, idx) => (
//               <ListItem
//                 key={idx}
//                 border="2px solid"
//                 borderColor="gray.200"
//                 borderRadius="md"
//                 p={1}
//                 display="flex"
//                 justifyContent="space-between"
//                 alignItems="center"
//               >
//                 <Button
//                   variant="link"
//                   colorScheme="blue"
//                   fontSize="sm"
//                   maxW="100%"
//                   overflow="hidden"
//                   textOverflow="ellipsis"
//                   whiteSpace="nowrap"
//                   onClick={() => {
//                     const url = URL.createObjectURL(file);
//                     window.open(url, "_blank");
//                   }}
//                 >
//                   {file.name}
//                 </Button>

//                 <Button
//                   size="sm"
//                   colorScheme="red"
//                   variant="ghost"
//                   onClick={() => {
//                     setFormData((prev) => ({
//                       ...prev,
//                       screenshot: prev.screenshot.filter((_, i) => i !== idx),
//                     }));
//                   }}
//                 >
//                   ✖
//                 </Button>
//               </ListItem>
//             ))}
//           </List>

//           <Button
//             type="submit"
//             colorScheme="green"
//             w="100%"
//             mt={3}
//             onClick={handleSave}
//             isDisabled={!formData.type || !formData.comment}
//           >
//             Submit Feedback
//           </Button>
//         </Box>
//       </Box>

//       {/* Modal */}
//       <Modal isOpen={isOpen} onClose={onClose} size="lg">
//         <ModalOverlay />
//         <ModalContent>
//           <ModalHeader>Add screenshot</ModalHeader>
//           <ModalCloseButton />
//           <ModalBody>
//             <Box
//               onDrop={handleDrop}
//               onDragOver={(e) => e.preventDefault()}
//               border="2px dashed"
//               borderColor="gray.300"
//               borderRadius="md"
//               p="6"
//               textAlign="center"
//               mb="4"
//             >
//               <Text fontWeight="bold" mb="2">
//                 Drag and drop images here
//               </Text>
//               <Text fontSize="sm" color="gray.500">
//                 or click below to browse
//               </Text>
//             </Box>
//             <Input type="file" multiple accept="image/*" p="1" onChange={handleFileSelect} />
//             {droppedFiles.length > 0 && (
//               <VStack align="start" mt="4" spacing="2">
//                 {droppedFiles.map((file, i) => (
//                   <Text key={i} fontSize="sm">
//                     ✅ {file.name}
//                   </Text>
//                 ))}
//               </VStack>
//             )}
//           </ModalBody>

//           <ModalFooter>
//             <Button colorScheme="blue" mr={3} onClick={handleAddImages}>
//               Add
//             </Button>
//             <Button variant="ghost" onClick={onClose}>
//               Cancel
//             </Button>
//           </ModalFooter>
//         </ModalContent>
//       </Modal>
//       <SerialNumber
//         isOpen={isOpen}
//         onClose={onClose}
//         onSave={handleSave}
//         formType="Customer"
//       />
//     </>
//   );
// }
